"use client";

import { Link } from "@/i18n/navigation";
import { formatMoney } from "@/lib/money-utils";
import { CartInfo, ProductInfo } from "@/types";
import { useLocale, useTranslations } from "next-intl";

import ArrowDown from "@/components/icons/arrow-down.svg";
import Heart from "@/components/icons/heart.svg";
import HeartSolid from "@/components/icons/heart-solid.svg";
import ShoppingCartSolid from "@/components/icons/shopping-cart-solid.svg";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/lib/client/storeHooks";
import { addToWishlist, removeFromWishlist } from "@/lib/client/wishlistSlice";
import UserSolid from "@/components/icons/user-solid.svg";
import { useEffect, useRef, useState } from "react";
import { ProductData } from "@/lib/sanity/responseTypes";
import { FetchResDesctructure } from "@/app/lib/fetch-utils";
import { loadCart } from "@/lib/client/cartSlice";
import ImageGallery from "./image-gallery";
import ProductSelector from "./product-selector";

interface ProductsGalleryProps {
  itemsPerRow: 2 | 5 | 7 | 9;
  itemWidth: number;
  imageHeight: number;
  products: ProductInfo[];
  loadMore?: () => void;
  hasMore?: boolean;
  hideVariantsCount?: boolean;
  hideShopControls?: boolean;
  doNotCenter?: boolean;
  disableMaxWidth?: boolean;
  scrollOnOverflow?: boolean;
  paddingY?: '10' | '20';
}

export default function ProductsGallery(props: ProductsGalleryProps) {
  const storeDispatch = useDispatch();
  const sessionState = useAppSelector(s => s.session);
  const wishlistStateSet = useAppSelector(s => s.wishlist.list.reduce((acc, val) => {
    acc[val.articleId] = val;
    return acc;
  }, {} as Record<number, ProductInfo>));

  const modalBtnRef = useRef<HTMLButtonElement | null>(null);
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const [modalProduct, setModalProduct] = useState<ProductInfo | null>(null);
  const [modalProductData, setModalProductData] = useState<ProductData | null>(null);

  const itemWidth = Math.round(props.itemWidth);
  const imageHeight = Math.round(props.imageHeight);

  const itemsPerRowClasses = {
    2: "md:grid-cols-2",
    5: "md:grid-cols-5",
    7: "md:grid-cols-7",
    9: "md:grid-cols-9",
  };

  const pyClasses = {
    '10': 'py-2.5',
    '20': 'py-5',
  }

  const controlsPriceBreak = {
    2: "flex-col xl:flex-row xl:items-center xl:justify-between",
    5: "flex-col xl:flex-row xl:items-center xl:justify-between",
    7: "flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between",
    9: "flex-col 3xl:flex-row 3xl:items-center 3xl:justify-between"
  };

  const t = useTranslations('Category');
  const tProduct = useTranslations('Product');
  const tUnits = useTranslations("PackagingUnits");
  const locale = useLocale();

  useEffect(() => {
    setModalProductData(null);

    if (modalProduct) {
      fetch('/api/list-product-data', { method: "POST", body: JSON.stringify({ product: `${modalProduct?.id}-${locale}`, locale: locale }) })
        .then(FetchResDesctructure<{ product: ProductData }>)
        .then(({ status, ok, body }) => {
          if (ok) {
            setModalProductData(body.product);
            modalBtnRef.current?.click();
            console.log('received product data: ', body);
          } else {
            setModalProductData(null);
          }
        });
    }
  }, [modalProduct]);

  const addToCart = (p: ProductInfo) => {
    setModalProduct(p);
    modalBtnRef.current?.click();
  }

  const doAddToCartFactory = (p: ProductData) => {
    return (qty: number) => {
      console.log(`Adding ${qty} units of ${p.code} to cart`);
      return fetch(`/api/cart/add/`, {
        method: "POST",
        body: JSON.stringify({ productId: p.code, productQuantity: qty })
      })
        .then(FetchResDesctructure<CartInfo>)
        .then(({ status, ok, body }) => {
          if (ok) {
            console.log('Added successfully to cart');
            storeDispatch(loadCart(body));
            (document.getElementById('cart_modal') as HTMLDialogElement).close()
          } else {
            console.error(`Error while adding to cart:\nStatus code: ${status}\nBody: ${body}`);
          }
        });
    }
  }

  async function toggleWishlist(product: ProductInfo) {
    if (!sessionState.user) {
      (document.getElementById('need_to_be_loggedin') as HTMLDialogElement).showModal()
      return;
    }

    if (!wishlistStateSet[product.articleId]) {
      //Product is not part of wishlist, need to add it
      let res = await fetch('/api/wishlist/add', {
        method: 'POST',
        body: JSON.stringify({ productId: product.articleId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
      const wishlistIds: number[] = await res.json();

      storeDispatch(addToWishlist(product));
    } else {
      //Product is already part of wishlist, need to remove it
      let res = await fetch('/api/wishlist/remove', {
        method: 'POST',
        body: JSON.stringify({ productId: product.articleId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}; ${JSON.stringify(await res.json())}`);
      const wishlistIds: number[] = await res.json();

      storeDispatch(removeFromWishlist(product));
    }
  }

  return (
    <div className={"text-black gap-5" + ((props.products.length >= 5 && !props.doNotCenter) ? " place-items-center" : " place-items-start") + (props.paddingY ? ` ${pyClasses[props.paddingY]}` : '')}>
      <div className={((props.products.length >= 5 && !props.doNotCenter) ? "justify-center " : " ") + (props.disableMaxWidth ? " " : "max-w-[1800px] ") + (props.scrollOnOverflow ? "overflow-y-scroll " : "flex-wrap ") + `grid grid-cols-2 ${itemsPerRowClasses[props.itemsPerRow]} ` + "w-full relative overflow-hidden items-start gap-x-5 xl:gap-x-[30px] gap-y-4 xl:gap-y-5 font-[Outfit]"}>
        {props.products.filter(p => p !== null && p !== undefined).map((p, i) => {
          return (
            <div key={`product-${i}`} className={`overflow-hidden shrink-0 flex flex-col items-center justify-center gap-2.5`}>
              <Link href={p.productPageUrl} className="flex-auto aspect-21/25 w-full h-auto max-w-full max-h-full rounded-2xl flex justify-center">
                <img src={`${p.imgSrc[0]}?w=${itemWidth * 3}&h=${imageHeight * 3}`} className={`rounded-2xl h-full w-auto max-w-full max-h-full object-cover`} />
              </Link>
              <div className="flex-1 p-0 w-full flex flex-col shrink-0 gap-2.5">
                <Link href={p.productPageUrl} className="text-base font-medium leading-[120%] self-stretch">{p.name}</Link>
                <div className="text-sm leading-[120%] self-stretch">{p.description}</div>
                {!props.hideShopControls && (
                  <div className={`overflow-hidden flex items-start gap-y-2.5 w-full ${controlsPriceBreak[props.itemsPerRow]}`}>
                    <div className="relative leading-[120%] text-sm">{p.price && formatMoney(p.price)}</div>
                    <div className="overflow-hidden flex flex-row items-start gap-2">
                      {wishlistStateSet[p.articleId] ? (
                        <button className="btn h-6" onClick={() => toggleWishlist(p)}>
                          <HeartSolid className="w-3.5 h-3.5 fill-secondary" />
                        </button>
                      ) : (
                        <button className="btn h-6" onClick={() => toggleWishlist(p)}>
                          <Heart className="w-3.5 h-3.5 stroke-black" />
                        </button>
                      )}
                      <button className="btn h-6" onClick={() => addToCart(p)}>
                        <ShoppingCartSolid className="w-3.5 h-3.5 fill-black" />
                      </button>
                    </div>
                  </div>
                )}
                {!props.hideVariantsCount && p.variantsCount && (<div className="w-full relative inline-block text-xs leading-[120%] text-left">{`${p.variantsCount} ${t('products-variants')}`}</div>) || null}
              </div>
            </div>
          )
        })}
      </div>
      {
        props.hasMore && props.loadMore && <div className="w-full relative overflow-hidden flex items-center justify-center py-5 border-box">
          <button className="btn btn-secondary text-white rounded-full gap-1.5" onClick={() => props.loadMore && props.loadMore()}>
            {t('products-show-more')}
            <ArrowDown className="w-3.5 h-3.5 stroke-white" />
          </button>
        </div>
      }
      <dialog id="need_to_be_loggedin" className="modal">
        <div className="modal-box w-11/12 xl:w-8/12">
          <h3 className="font-bold text-lg font-[Outfit]">{t('wishlist-when-logged-in-only-title')}</h3>
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          <div className="flex flex-col items-center gap-2.5 w-full">
            <div className="p-2.5">
              {t("wishlist-when-logged-in-only")}
            </div>
            <div className="p-2.5">
              <Link href="/kundenkonto/login">
                <button className={"btn btn-secondary text-white rounded-full"}>
                  <UserSolid height={14} width={14} className="stroke-white" />
                  {t("login-or-signup")}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </dialog>
      <button className="btn hidden" onClick={() => modalRef.current && modalRef.current.showModal()} ref={modalBtnRef}></button>
      <dialog id="cart_modal" className="modal" ref={modalRef}>
        <div className="modal-box w-11/12 max-w-5xl">
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
          </form>
          {modalProductData && (
            <div className="w-full relative overflow-hidden flex md:flex-row flex-col align-start py-2.5 px-0 box-border gap-[30px] text-left text-sm font-[Outfit]">
              <ImageGallery coverImage={modalProductData.coverImage} otherImages={modalProductData.image || []} />
              <ProductSelector product={modalProductData} t={tProduct} tUnits={tUnits} locale={locale} addToCart={doAddToCartFactory(modalProductData)} toggleWishlist={() => modalProduct && toggleWishlist(modalProduct)} />
            </div>
          )}
          {!modalProductData && (
            <div className="h-[300px] flex flex-col items-center justify-center">
              <span className="loading loading-spinner loading-xl text-primary"></span>
            </div>
          )}
        </div>
      </dialog>
    </div >
  )
}
